/**
 * Token Manager - Cookie-Only Authentication Mode
 *
 * Manages tenant-scoped user state while the backend owns the real session
 * via HttpOnly cookies. No tokens are stored or read client-side anymore.
 */

import {
  getCurrentOrgSlug,
  getCurrentClientSlug,
  getCurrentBranchSlug,
  getTenantScopedStorageKey,
  getTenantSessionIndicatorKey,
} from '../../utils/tenant';
import { hostBelongsToOrgSlug } from '../../utils/subdomain';

const BROADCAST_CHANNEL = 'auth_sync';

class TokenManager {
  constructor() {
    this.getOrgSlug = () => getCurrentOrgSlug();
    this.currentToken = null;
    this.currentRefreshToken = null;
    this.currentSessionToken = null;
    this.broadcastChannel =
      typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(BROADCAST_CHANNEL) : null;

    if (this.broadcastChannel) {
      this.setupBroadcastListener();
    }

    this.setupStorageListener();
  }

  /**
   * Retrieve user data for the current tenant from storage.
   */
  getUserData() {
    const orgSlug = this.getOrgSlug();
    const userKey = getTenantScopedStorageKey('user', orgSlug);
    const raw = localStorage.getItem(userKey);

    if (!raw) {
      return null;
    }

    try {
      const user = JSON.parse(raw);
      const clientSlug = getCurrentClientSlug();
      const branchSlug = getCurrentBranchSlug();
      const userOrgSlug = user?.organization_slug || user?.organizationSlug;
      const userClientSlug = user?.client_organization_slug || user?.clientOrganizationSlug;
      const allowedTenantSlugs = new Set(
        [orgSlug, clientSlug, branchSlug, userClientSlug].filter(Boolean)
      );

      // allowedTenantSlugs comes from parseHostname's first-hyphen split, which
      // mis-parses hyphenated labels ("austin-branch-loaninghub" -> "austin" /
      // "branch-loaninghub"), so also accept a user whose org owns this host.
      if (
        userOrgSlug &&
        !allowedTenantSlugs.has(userOrgSlug) &&
        !hostBelongsToOrgSlug(userOrgSlug) &&
        orgSlug !== 'platform'
      ) {
        localStorage.removeItem(userKey);
        sessionStorage.removeItem(userKey);
        return null;
      }

      return user;
    } catch (error) {
      if (!import.meta.env.PROD) {
        console.error('[TokenManager] Failed to parse user data', error);
      }
      localStorage.removeItem(userKey);
      sessionStorage.removeItem(userKey);
      return null;
    }
  }

  /**
   * Store user data for the current tenant.
   */
  setUserData(user) {
    const orgSlug = this.getOrgSlug();
    const userKey = getTenantScopedStorageKey('user', orgSlug);
    const roleKey = getTenantScopedStorageKey('role', orgSlug);
    const selectionKey = getTenantScopedStorageKey('userSelection', orgSlug);
    const expectedRoleKey = getTenantScopedStorageKey('expectedUserRole', orgSlug);
    const sessionKey = getTenantSessionIndicatorKey(orgSlug);
    const sessionTokenKey = getTenantScopedStorageKey('sessionToken', orgSlug);

    if (!user) {
      this.clearUserData();
      return;
    }

    const serialized = JSON.stringify(user);
    localStorage.setItem(userKey, serialized);
    sessionStorage.setItem(userKey, serialized);

    const role = user.role || 'USER';
    localStorage.setItem(roleKey, role);
    sessionStorage.setItem(roleKey, role);

    localStorage.setItem(selectionKey, role);
    sessionStorage.setItem(selectionKey, role);
    localStorage.setItem(expectedRoleKey, role);
    sessionStorage.setItem(expectedRoleKey, role);

    localStorage.setItem(sessionKey, 'true');

    if (!import.meta.env.PROD) {
      console.log('[TokenManager] Stored user for org', orgSlug);
    }

    this.broadcast('USER_UPDATED', { org: orgSlug });

    window.dispatchEvent(
      new CustomEvent('userDataUpdated', {
        detail: { user, org: orgSlug },
      })
    );
  }

  /**
   * Determine if a user is currently authenticated client-side.
   */
  isAuthenticated() {
    return !!this.getUserData();
  }

  /**
   * Clear all stored auth state for the current tenant.
   */
  clearUserData(options = {}) {
    const { suppressBroadcast = false } = options;
    const orgSlug = this.getOrgSlug();

    const userKey = getTenantScopedStorageKey('user', orgSlug);
    const roleKey = getTenantScopedStorageKey('role', orgSlug);
    const organizationKey = getTenantScopedStorageKey('organization', orgSlug);
    const selectionKey = getTenantScopedStorageKey('userSelection', orgSlug);
    const expectedRoleKey = getTenantScopedStorageKey('expectedUserRole', orgSlug);
    const sessionKey = getTenantSessionIndicatorKey(orgSlug);
    const sessionTokenKey = getTenantScopedStorageKey('sessionToken', orgSlug);
    const redirectKey = orgSlug ? getTenantScopedStorageKey('redirectAfterLogin', orgSlug) : null;

    [localStorage, sessionStorage].forEach((storage) => {
      storage.removeItem(userKey);
      storage.removeItem(roleKey);
      storage.removeItem(organizationKey);
      storage.removeItem(selectionKey);
      storage.removeItem(expectedRoleKey);
      storage.removeItem(sessionTokenKey);
      if (redirectKey) {
        storage.removeItem(redirectKey);
      }
    });

    if (sessionKey) {
      localStorage.removeItem(sessionKey);
    }
    sessionStorage.removeItem('redirectAfterLogin');
    localStorage.removeItem('redirectAfterLogin');

    // Legacy fallbacks (remove once consumers migrate to tenant keys)
    ['organization', 'userRole', 'expectedUserRole', 'current_organization', 'org_cache_timestamp']
      .forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

    this.currentToken = null;
    this.currentRefreshToken = null;
    this.currentSessionToken = null;

    if (!suppressBroadcast) {
      this.broadcast('USER_DATA_CLEARED', { org: orgSlug });
    }
  }

  /**
   * Alias maintained for backwards compatibility.
   */
  clearToken(options = {}) {
    const { suppressBroadcast = false } = options;
    this.clearUserData({ suppressBroadcast: true });

    this.currentToken = null;
    this.currentRefreshToken = null;
    this.currentSessionToken = null;

    if (!suppressBroadcast) {
      this.broadcast('USER_LOGOUT', { org: this.getOrgSlug() });
    }
  }

  /**
   * Broadcast helper that includes org and timestamp.
   */
  broadcast(type, payload = {}) {
    if (!this.broadcastChannel) return;

    this.broadcastChannel.postMessage({
      type,
      timestamp: Date.now(),
      ...payload,
    });
  }

  /**
   * Listen for auth events from other tabs.
   */
  setupBroadcastListener() {
    this.broadcastChannel.onmessage = (event) => {
      const { type, org } = event.data || {};
      const currentOrg = this.getOrgSlug();

      if (org && org !== currentOrg) {
        return;
      }

      switch (type) {
        case 'USER_UPDATED':
          window.dispatchEvent(
            new CustomEvent('userDataSync', {
              detail: { org: currentOrg },
            })
          );
          break;
        case 'USER_DATA_CLEARED':
        case 'USER_LOGOUT':
          this.clearUserData({ suppressBroadcast: true });
          this.currentToken = null;
          this.currentRefreshToken = null;
          this.currentSessionToken = null;
          window.dispatchEvent(
            new CustomEvent('userDataCleared', {
              detail: { org: currentOrg, reason: type },
            })
          );
          break;
        default:
          break;
      }
    };
  }

  /**
   * Keep tabs in sync when storage changes.
   */
  setupStorageListener() {
    window.addEventListener('storage', (event) => {
      if (!event.key) return;

      const orgSlug = this.getOrgSlug();
      const userKey = getTenantScopedStorageKey('user', orgSlug);
      const sessionKey = getTenantSessionIndicatorKey(orgSlug);
      const sessionTokenKey = getTenantScopedStorageKey('sessionToken', orgSlug);

      if (event.key === userKey && event.newValue) {
        try {
          const user = JSON.parse(event.newValue);
          window.dispatchEvent(
            new CustomEvent('userDataUpdated', {
              detail: { user, org: orgSlug },
            })
          );
        } catch (error) {
          if (!import.meta.env.PROD) {
            console.error('[TokenManager] Failed to parse user data from storage event', error);
          }
        }
      }

      if ((event.key === userKey && !event.newValue) || (event.key === sessionKey && event.newValue !== 'true')) {
        this.clearUserData({ suppressBroadcast: true });
        this.currentToken = null;
        this.currentRefreshToken = null;
        this.currentSessionToken = null;
        window.dispatchEvent(
          new CustomEvent('userDataCleared', {
            detail: { org: orgSlug, reason: 'storage' },
          })
        );
      }

      if (event.key === sessionTokenKey) {
        if (!event.newValue) {
          this.currentSessionToken = null;
        } else {
          this.currentSessionToken = event.newValue;
        }
      }
    });
  }

  /**
   * Methods kept for backwards compatibility with existing callers.
   * They now reflect cookie-only behaviour.
   */
  getToken() {
    if (!this.currentToken) {
      return null;
    }

    return {
      token: this.currentToken,
      refresh_token: this.currentRefreshToken,
      user: this.getUserData(),
    };
  }

  getAccessToken() {
    return this.currentToken;
  }

  getSessionToken() {
    if (this.currentSessionToken) {
      return this.currentSessionToken;
    }

    const orgSlug = this.getOrgSlug();
    const sessionTokenKey = getTenantScopedStorageKey('sessionToken', orgSlug);

    const stored =
      typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(sessionTokenKey)
        : null;

    if (stored) {
      this.currentSessionToken = stored;
      return stored;
    }

    return null;
  }

  getRefreshToken() {
    return this.currentRefreshToken;
  }

  setToken(token, user, refreshToken = null, isSessionToken = false) {
    if (token) {
      this.currentToken = token;
    }

    if (refreshToken) {
      this.currentRefreshToken = refreshToken;
    }

    if (isSessionToken && token) {
      this.currentSessionToken = token;
      const sessionTokenKey = getTenantScopedStorageKey('sessionToken', this.getOrgSlug());
      try {
        sessionStorage.setItem(sessionTokenKey, token);
      } catch (error) {
        if (!import.meta.env.PROD) {
          console.warn('[TokenManager] Unable to persist session token', error);
        }
      }
    }

    this.setUserData(user);
    return null;
  }

  setSessionToken(sessionToken) {
    const orgSlug = this.getOrgSlug();
    const sessionTokenKey = getTenantScopedStorageKey('sessionToken', orgSlug);

    if (!sessionToken) {
      this.currentSessionToken = null;
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(sessionTokenKey);
      }
      return;
    }

    this.currentSessionToken = sessionToken;
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem(sessionTokenKey, sessionToken);
      } catch (error) {
        if (!import.meta.env.PROD) {
          console.warn('[TokenManager] Unable to persist session token', error);
        }
      }
    }
  }

  parseJwt() {
    return null;
  }

  getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
    };
  }

  hasPermission(permission) {
    const userData = this.getUserData();
    if (!userData?.permissions) return false;
    return userData.permissions.includes(permission);
  }

  hasRole(role) {
    const userData = this.getUserData();
    return userData?.role === role;
  }

  isB2BUser() {
    const userData = this.getUserData();
    return Boolean(userData?.isB2B);
  }

  isB2CUser() {
    const userData = this.getUserData();
    return Boolean(userData?.isB2C);
  }
}

export default new TokenManager();
